import React, { useState, useEffect } from 'react'
import {
  BookOpen,
  AlertCircle,
  CheckCircle,
  Camera,
  Sun,
  Eye,
  Smartphone,
  Target,
  User,
  X,
  Menu,
  ChevronRight,
  Lightbulb,
  Focus,
  Move,
  Zap,
  Shield,
  Package,
  Glasses
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/utils/cn'
import Tooltip from '@/components/Tooltip'
import { useTranslation } from 'react-i18next'

const GuidelinesPage: React.FC = () => {
  const { t } = useTranslation()
  const { isAdmin } = useAuth()
  // Sidebar closed by default on mobile, open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1024)
  const [activeSection, setActiveSection] = useState('')
  const [selectedImage, setSelectedImage] = useState<{ src: string; alt: string } | null>(null)

  // Guidelines table of contents structure
  const tableOfContents = [
    {
      id: 'remove-obstructions',
      title: 'Remove Obstructions',
      icon: <Package className="h-4 w-4" />
    },
    {
      id: 'proper-placement',
      title: 'Proper Placement',
      icon: <Target className="h-4 w-4" />
    },
    {
      id: 'avoid-reflections',
      title: 'Avoid Reflections',
      icon: <Glasses className="h-4 w-4" />
    },
    {
      id: 'lighting-conditions',
      title: 'Lighting Conditions',
      icon: <Sun className="h-4 w-4" />
    },
    {
      id: 'image-clarity',
      title: 'Image Clarity',
      icon: <Eye className="h-4 w-4" />
    },
    {
      id: 'camera-stability',
      title: 'Camera Stability',
      icon: <Smartphone className="h-4 w-4" />
    },
    {
      id: 'optimal-distance',
      title: 'Optimal Distance',
      icon: <Focus className="h-4 w-4" />
    },
    {
      id: 'ergonomic-position',
      title: 'Ergonomic Position',
      icon: <User className="h-4 w-4" />
    },
    {
      id: 'review-results',
      title: 'Review Results',
      icon: <Shield className="h-4 w-4" />
    }
  ]

  // Scroll handler for active section detection
  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.map(item => item.id)
      const scrollPosition = window.scrollY + 100

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId)
        if (element) {
          const { offsetTop, offsetHeight } = element
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(sectionId)
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Set initial active section
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // ESC key handler for closing image modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedImage) {
        setSelectedImage(null)
      }
    }

    if (selectedImage) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedImage])

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
      // Close sidebar on mobile after selecting a section
      if (window.innerWidth < 1024) {
        setSidebarOpen(false)
      }
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={cn(
        'fixed top-16 bottom-0 left-0 z-40 w-80 bg-white shadow-lg transform transition-transform duration-300 lg:sticky lg:top-20 lg:h-screen lg:translate-x-0 flex flex-col',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <div className="flex items-center">
            <BookOpen className="h-6 w-6 text-blue-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">Photography Guidelines</h2>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="p-6 overflow-y-auto flex-1">
          <div className="space-y-2">
            {tableOfContents.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={cn(
                  'flex items-center w-full p-3 text-left rounded-lg transition-colors',
                  activeSection === section.id
                    ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                )}
              >
                <div className="flex items-center flex-1">
                  {section.icon}
                  <span className="ml-3 text-sm font-medium">{section.title}</span>
                </div>
                <ChevronRight className="h-4 w-4 opacity-50" />
              </button>
            ))}
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 lg:ml-0 flex flex-col min-h-screen">
        {/* Mobile Header - Sticky */}
        <div className="lg:hidden bg-white shadow-sm border-b px-4 py-3 flex items-center justify-between sticky top-16 z-20">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-md text-gray-500 hover:text-gray-700"
          >
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">Guidelines</h1>
          <div className="w-9" /> {/* Spacer */}
        </div>

        {/* Content - Add bottom padding for mobile tab bar */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 lg:pb-8 space-y-8 flex-1">
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center space-x-3">
              <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <Camera className="h-6 w-6 text-primary-600" />
              </div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Photography Guidelines</h1>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              For optimal algorithm performance, please follow these essential photography guidelines.
              These simple but important rules will ensure the highest accuracy in object detection and classification.
            </p>
          </div>

          {/* Overview */}
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Lightbulb className="h-5 w-5 mr-2 text-primary-600" />
              Overview
            </h2>
            <p className="text-gray-600">
              Following these guidelines will significantly improve detection accuracy and reduce the need for manual corrections.
              Use the sidebar navigation to explore each section, or scroll through this complete guide to master your jewelry photography techniques.
            </p>
          </div>

          {/* Guidelines Sections */}

          {/* Section 1: Remove Obstructions */}
          <section id="remove-obstructions" className="mb-16">
            <div className="flex items-center mb-6">
              <div className="bg-red-100 p-3 rounded-lg mr-4">
                <X className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Clear the Showcase</h2>
                <p className="text-gray-600">Remove any objects that obstruct the view</p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-8">
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6">
                  <strong className="text-red-600">Remove any foreign objects</strong> from the showcase that may obstruct or interfere with the direct view of jewelry items.
                  Clean showcases lead to cleaner detections.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <h4 className="font-semibold text-red-800 mb-3 flex items-center">
                      <X className="h-4 w-4 mr-2" />
                      Avoid These
                    </h4>
                    <ul className="text-red-700 space-y-2">
                      <li>• Price tags covering jewelry</li>
                      <li>• Cleaning cloths left in showcase</li>
                      <li>• Personal items (phones, keys, etc.)</li>
                      <li>• Dust, fingerprints on glass</li>
                      <li>• Reflective stickers or labels</li>
                    </ul>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Best Practices
                    </h4>
                    <ul className="text-green-700 space-y-2">
                      <li>• Clean glass surfaces thoroughly</li>
                      <li>• Remove all temporary items</li>
                      <li>• Ensure clear, unobstructed view</li>
                      <li>• Check for reflection interference</li>
                      <li>• Maintain showcase organization</li>
                    </ul>
                  </div>
                </div>
                {/* Example images */}
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-800 mb-4">📷 Visual Examples</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h5 className="font-medium text-red-800 mb-3 flex items-center">
                        <X className="h-4 w-4 mr-2" />
                        Obstructed View
                      </h5>
                      <img
                        src="/guidelines/obstructions/obstruction.jpg"
                        alt="Example of obstructed jewelry showcase"
                        className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setSelectedImage({ src: '/guidelines/obstructions/obstruction.jpg', alt: 'Example of obstructed jewelry showcase' })}
                      />
                      <p className="text-sm text-red-700 mt-2">
                        Objects blocking the view of jewelry items make detection difficult or impossible.
                      </p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-medium text-green-800 mb-3 flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Clear View
                      </h5>
                      <div className="w-full h-48 bg-green-100 rounded-lg border flex items-center justify-center">
                        <div className="text-center text-green-600">
                          <Camera className="h-8 w-8 mx-auto mb-2" />
                          <p className="text-sm">Clear, unobstructed<br/>showcase view</p>
                        </div>
                      </div>
                      <p className="text-sm text-green-700 mt-2">
                        Clean, clear view allows the algorithm to detect all jewelry items accurately.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Proper Placement */}
          <section id="proper-placement" className="mb-16">
            <div className="flex items-center mb-6">
              <div className="bg-blue-100 p-3 rounded-lg mr-4">
                <Target className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Proper Item Placement</h2>
                <p className="text-gray-600">Position items in their designated locations</p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-8">
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6">
                  <strong className="text-blue-600">Place all jewelry items back where they belong</strong> and are expected to be found.
                  Proper organization helps the algorithm recognize items in their typical context and positioning.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                    <Target className="h-4 w-4 mr-2" />
                    Pro Tips
                  </h4>
                  <ul className="text-blue-700 space-y-2">
                    <li>• <strong>Rings:</strong> Place in designated ring trays or holders</li>
                    <li>• <strong>Necklaces:</strong> Hang properly on display stands to show full length</li>
                    <li>• <strong>Bracelets:</strong> Lay flat or use bracelet displays to show complete circle</li>
                    <li>• <strong>Earrings:</strong> Use earring cards or stands to display pairs together</li>
                    <li>• <strong>Watches:</strong> Position with face clearly visible and band extended</li>
                  </ul>
                </div>
                {/* Example images */}
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-800 mb-4">📷 Visual Examples</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h5 className="font-medium text-red-800 mb-3 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Partial View
                      </h5>
                      <img
                        src="/guidelines/placement/partial.jpg"
                        alt="Example of partially visible jewelry"
                        className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setSelectedImage({ src: '/guidelines/placement/partial.jpg', alt: 'Example of partially visible jewelry' })}
                      />
                      <p className="text-sm text-red-700 mt-2">
                        Items not fully visible or improperly positioned reduce detection accuracy.
                      </p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-medium text-green-800 mb-3 flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Proper Placement
                      </h5>
                      <div className="w-full h-48 bg-green-100 rounded-lg border flex items-center justify-center">
                        <div className="text-center text-green-600">
                          <Target className="h-8 w-8 mx-auto mb-2" />
                          <p className="text-sm">Well-organized<br/>jewelry display</p>
                        </div>
                      </div>
                      <p className="text-sm text-green-700 mt-2">
                        Items properly positioned in their designated areas enable optimal detection.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Avoid Reflections */}
          <section id="avoid-reflections" className="mb-16">
            <div className="flex items-center mb-6">
              <div className="bg-indigo-100 p-3 rounded-lg mr-4">
                <Glasses className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Avoid Mirror Reflections</h2>
                <p className="text-gray-600">Prevent double counting from reflective surfaces</p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-8">
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6">
                  <strong className="text-indigo-600">Be careful of mirrors and reflective surfaces</strong> in your showcases.
                  When jewelry appears in both direct view and reflected in mirrors, the algorithm may mistakenly count the same item twice, leading to inaccurate inventory counts.
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                  <h4 className="font-semibold text-red-800 mb-3 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    Double Counting Risk
                  </h4>
                  <p className="text-red-700 mb-4">
                    The AI system may detect the same piece of jewelry twice: once as the actual item and once as its reflection, resulting in duplicate entries in your inventory system.
                  </p>
                  <ul className="text-red-700 space-y-2">
                    <li>• Mirror backs in showcase displays</li>
                    <li>• Reflective glass surfaces</li>
                    <li>• Polished metal display stands</li>
                    <li>• Mirrored showcase floors or walls</li>
                    <li>• Glass table tops with reflections</li>
                  </ul>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Best Practices
                    </h4>
                    <ul className="text-green-700 space-y-2">
                      <li>• Position camera to minimize mirror visibility</li>
                      <li>• Angle shots to avoid reflective surfaces</li>
                      <li>• Use matte backdrop when possible</li>
                      <li>• Cover mirrors temporarily during photography</li>
                      <li>• Take multiple angles if mirrors are unavoidable</li>
                    </ul>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                      <Glasses className="h-4 w-4 mr-2" />
                      Solutions
                    </h4>
                    <ul className="text-blue-700 space-y-2">
                      <li>• <strong>Change angle:</strong> Shoot from side to avoid mirror</li>
                      <li>• <strong>Temporary covering:</strong> Use cloth to block reflection</li>
                      <li>• <strong>Strategic positioning:</strong> Move items away from mirrors</li>
                      <li>• <strong>Review carefully:</strong> Check for duplicate detections</li>
                      <li>• <strong>Delete duplicates:</strong> Remove false positive detections</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-semibold text-yellow-800 mb-3 flex items-center">
                    <Lightbulb className="h-4 w-4 mr-2" />
                    Pro Tip
                  </h4>
                  <p className="text-yellow-800">
                    <strong>If reflections are unavoidable</strong>, take note during review phase and manually delete any duplicate detections.
                    The system shows confidence levels - reflected items often have lower confidence scores and can be identified for removal.
                  </p>
                </div>
                {/* Example images */}
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-800 mb-4">📷 Visual Examples</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h5 className="font-medium text-red-800 mb-3 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Mirror Reflections
                      </h5>
                      <img
                        src="/guidelines/reflections/mirror.jpg"
                        alt="Example of jewelry reflected in mirror causing double detection"
                        className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setSelectedImage({ src: '/guidelines/reflections/mirror.jpg', alt: 'Example of jewelry reflected in mirror causing double detection' })}
                      />
                      <p className="text-sm text-red-700 mt-2">
                        Mirror reflections can cause the same jewelry to be detected twice, leading to inaccurate counts.
                      </p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-medium text-green-800 mb-3 flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        No Reflections
                      </h5>
                      <div className="w-full h-48 bg-green-100 rounded-lg border flex items-center justify-center">
                        <div className="text-center text-green-600">
                          <Glasses className="h-8 w-8 mx-auto mb-2" />
                          <p className="text-sm">Mirror-free<br/>photography angle</p>
                        </div>
                      </div>
                      <p className="text-sm text-green-700 mt-2">
                        Angle the camera to avoid reflective surfaces and prevent double counting.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Lighting Conditions */}
          <section id="lighting-conditions" className="mb-16">
            <div className="flex items-center mb-6">
              <div className="bg-yellow-100 p-3 rounded-lg mr-4">
                <Sun className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Optimal Lighting</h2>
                <p className="text-gray-600">Ensure good ambient light conditions</p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-8">
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6">
                  <strong className="text-yellow-600">Ensure adequate ambient lighting</strong> so the captured image is neither extremely dark nor overly bright.
                  Well-lit jewelry shows true colors and fine details that are crucial for accurate classification.
                </p>
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-2">🌑</div>
                    <h4 className="font-semibold text-red-800 mb-2">Too Dark</h4>
                    <p className="text-red-700 text-sm">Details lost in shadows, metals appear black</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-2">💡</div>
                    <h4 className="font-semibold text-green-800 mb-2">Just Right</h4>
                    <p className="text-green-700 text-sm">Even lighting, true colors, clear details</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-2">☀️</div>
                    <h4 className="font-semibold text-red-800 mb-2">Too Bright</h4>
                    <p className="text-red-700 text-sm">Overexposed, reflections, washed out colors</p>
                  </div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                  <h4 className="font-semibold text-yellow-800 mb-3 flex items-center">
                    <Lightbulb className="h-4 w-4 mr-2" />
                    Lighting Tips
                  </h4>
                  <ul className="text-yellow-800 space-y-2">
                    <li>• Use store's existing showcase lighting</li>
                    <li>• Avoid direct sunlight or harsh shadows</li>
                    <li>• Turn on additional lights if image appears too dark</li>
                    <li>• Check phone screen preview before capturing</li>
                    <li>• Consider multiple angles if lighting is uneven</li>
                  </ul>
                </div>
                {/* Example images */}
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-800 mb-4">📷 Visual Examples</h4>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h5 className="font-medium text-red-800 mb-3 text-center">
                        Too Dark
                      </h5>
                      <div className="w-full h-32 bg-gray-800 rounded-lg border flex items-center justify-center">
                        <div className="text-center text-gray-400">
                          <Eye className="h-6 w-6 mx-auto mb-1" />
                          <p className="text-xs">Poor visibility</p>
                        </div>
                      </div>
                      <p className="text-xs text-red-700 mt-2 text-center">
                        Details lost in shadows
                      </p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-medium text-green-800 mb-3 text-center">
                        Perfect Light
                      </h5>
                      <div className="w-full h-32 bg-green-100 rounded-lg border flex items-center justify-center">
                        <div className="text-center text-green-600">
                          <Sun className="h-6 w-6 mx-auto mb-1" />
                          <p className="text-xs">Clear details</p>
                        </div>
                      </div>
                      <p className="text-xs text-green-700 mt-2 text-center">
                        Even lighting, true colors
                      </p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h5 className="font-medium text-red-800 mb-3 text-center">
                        Too Bright
                      </h5>
                      <div className="w-full h-32 bg-yellow-100 rounded-lg border flex items-center justify-center">
                        <div className="text-center text-yellow-600">
                          <Zap className="h-6 w-6 mx-auto mb-1" />
                          <p className="text-xs">Overexposed</p>
                        </div>
                      </div>
                      <p className="text-xs text-red-700 mt-2 text-center">
                        Washed out colors
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Image Clarity */}
          <section id="image-clarity" className="mb-16">
            <div className="flex items-center mb-6">
              <div className="bg-purple-100 p-3 rounded-lg mr-4">
                <Eye className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Sharp & Complete Objects</h2>
                <p className="text-gray-600">Ensure all objects are in focus and fully visible</p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-8">
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6">
                  <strong className="text-purple-600">Ensure all objects appear sharp and in full view</strong> on your phone screen.
                  The algorithm is designed to recognize complete, in-focus objects. Partially visible or blurry items may be missed or misclassified.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                    <h4 className="font-semibold text-purple-800 mb-3 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Optimal Clarity
                    </h4>
                    <ul className="text-purple-700 space-y-2">
                      <li>• All jewelry items completely visible</li>
                      <li>• Sharp focus throughout the image</li>
                      <li>• No motion blur from camera shake</li>
                      <li>• Details like stones and patterns clear</li>
                      <li>• No items cut off at image edges</li>
                    </ul>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                    <h4 className="font-semibold text-orange-800 mb-3 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Avoid These Issues
                    </h4>
                    <ul className="text-orange-700 space-y-2">
                      <li>• Blurry or out-of-focus items</li>
                      <li>• Partially visible jewelry (cut off)</li>
                      <li>• Items obscured by other objects</li>
                      <li>• Motion blur from unsteady hands</li>
                      <li>• Depth of field issues (selective focus)</li>
                    </ul>
                  </div>
                </div>
                {/* Example images */}
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-800 mb-4">📷 Visual Examples</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h5 className="font-medium text-red-800 mb-3 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Blurry Image
                      </h5>
                      <img
                        src="/guidelines/clarity/blur.jpg"
                        alt="Example of blurry jewelry image"
                        className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setSelectedImage({ src: '/guidelines/clarity/blur.jpg', alt: 'Example of blurry jewelry image' })}
                      />
                      <p className="text-sm text-red-700 mt-2">
                        Blurry or out-of-focus images make it difficult for the algorithm to identify details.
                      </p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-medium text-green-800 mb-3 flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Sharp & Clear
                      </h5>
                      <div className="w-full h-48 bg-green-100 rounded-lg border flex items-center justify-center">
                        <div className="text-center text-green-600">
                          <Eye className="h-8 w-8 mx-auto mb-2" />
                          <p className="text-sm">Crystal clear<br/>sharp focus</p>
                        </div>
                      </div>
                      <p className="text-sm text-green-700 mt-2">
                        Sharp, in-focus images allow the algorithm to see all details clearly.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Camera Stability */}
          <section id="camera-stability" className="mb-16">
            <div className="flex items-center mb-6">
              <div className="bg-green-100 p-3 rounded-lg mr-4">
                <Smartphone className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Steady Photography</h2>
                <p className="text-gray-600">Hold your phone steady while capturing</p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-8">
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6">
                  <strong className="text-green-600">Hold your phone steadily</strong> while taking images to avoid motion blur and ensure sharp details.
                  Stable photography is essential for the algorithm to properly analyze fine jewelry features.
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                    <Move className="h-4 w-4 mr-2" />
                    Stability Techniques
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <ul className="text-green-700 space-y-2">
                      <li>• <strong>Two-hand grip:</strong> Use both hands to hold phone</li>
                      <li>• <strong>Brace yourself:</strong> Lean against counter or wall</li>
                      <li>• <strong>Breath control:</strong> Take photo during natural breath pause</li>
                    </ul>
                    <ul className="text-green-700 space-y-2">
                      <li>• <strong>Timer function:</strong> Use 2-3 second timer if needed</li>
                      <li>• <strong>Gentle tap:</strong> Press shutter button softly</li>
                      <li>• <strong>Check preview:</strong> Review image immediately after capture</li>
                    </ul>
                  </div>
                </div>
                {/* Example images */}
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-800 mb-4">📷 Visual Examples</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-medium text-green-800 mb-3 flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Steady Shot
                      </h5>
                      <div className="w-full h-48 bg-green-100 rounded-lg border flex items-center justify-center">
                        <div className="text-center text-green-600">
                          <Smartphone className="h-8 w-8 mx-auto mb-2" />
                          <p className="text-sm">Stable, sharp<br/>photography</p>
                        </div>
                      </div>
                      <p className="text-sm text-green-700 mt-2">
                        Steady hands result in crisp, clear images perfect for detection.
                      </p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h5 className="font-medium text-red-800 mb-3 flex items-center">
                        <X className="h-4 w-4 mr-2" />
                        Camera Shake
                      </h5>
                      <div className="w-full h-48 bg-red-100 rounded-lg border flex items-center justify-center">
                        <div className="text-center text-red-600">
                          <Move className="h-8 w-8 mx-auto mb-2" />
                          <p className="text-sm">Motion blur<br/>from shaking</p>
                        </div>
                      </div>
                      <p className="text-sm text-red-700 mt-2">
                        Camera shake causes motion blur, making objects difficult to detect.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6: Optimal Distance */}
          <section id="optimal-distance" className="mb-16">
            <div className="flex items-center mb-6">
              <div className="bg-indigo-100 p-3 rounded-lg mr-4">
                <Focus className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Medium to Close Distance</h2>
                <p className="text-gray-600">Capture from appropriate distance for mobile cameras</p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-8">
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6">
                  <strong className="text-indigo-600">Take pictures from medium or close distance</strong>, never from far away.
                  Mobile phone cameras have limitations, and distant shots result in small objects that are difficult for the algorithm to analyze accurately.
                </p>
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-2">🔍</div>
                    <h4 className="font-semibold text-red-800 mb-2">Too Far</h4>
                    <p className="text-red-700 text-sm">Objects too small, details lost, poor recognition</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-2">📐</div>
                    <h4 className="font-semibold text-green-800 mb-2">Medium Distance</h4>
                    <p className="text-green-700 text-sm">Perfect balance, clear details, full context</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <div className="text-2xl mb-2">🔎</div>
                    <h4 className="font-semibold text-green-800 mb-2">Close Distance</h4>
                    <p className="text-green-700 text-sm">Maximum detail, ideal for fine jewelry</p>
                  </div>
                </div>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
                  <h4 className="font-semibold text-indigo-800 mb-3 flex items-center">
                    <Camera className="h-4 w-4 mr-2" />
                    Distance Guidelines
                  </h4>
                  <ul className="text-indigo-800 space-y-2">
                    <li>• <strong>Ideal range:</strong> 1-3 feet (30-90 cm) from showcase</li>
                    <li>• <strong>Jewelry should fill:</strong> 30-60% of the phone screen</li>
                    <li>• <strong>Include context:</strong> Show surrounding area for reference</li>
                    <li>• <strong>Avoid:</strong> Shooting from across the room or store</li>
                    <li>• <strong>Test different distances:</strong> Find what works best for your showcase</li>
                  </ul>
                </div>
                {/* Example images */}
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-800 mb-4">📷 Visual Examples</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h5 className="font-medium text-red-800 mb-3 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-2" />
                        Not In Place / Too Far
                      </h5>
                      <img
                        src="/guidelines/distance/not_in_place.jpg"
                        alt="Example of jewelry not in proper place or shot from too far"
                        className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setSelectedImage({ src: '/guidelines/distance/not_in_place.jpg', alt: 'Example of jewelry not in proper place or shot from too far' })}
                      />
                      <p className="text-sm text-red-700 mt-2">
                        Items too far away or not properly positioned make detection difficult.
                      </p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-medium text-green-800 mb-3 flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Optimal Distance
                      </h5>
                      <div className="w-full h-48 bg-green-100 rounded-lg border flex items-center justify-center">
                        <div className="text-center text-green-600">
                          <Focus className="h-8 w-8 mx-auto mb-2" />
                          <p className="text-sm">Perfect distance<br/>clear details</p>
                        </div>
                      </div>
                      <p className="text-sm text-green-700 mt-2">
                        Medium to close distance captures all details while maintaining context.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 7: Ergonomic Position */}
          <section id="ergonomic-position" className="mb-16">
            <div className="flex items-center mb-6">
              <div className="bg-teal-100 p-3 rounded-lg mr-4">
                <User className="h-6 w-6 text-teal-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Natural Standing Position</h2>
                <p className="text-gray-600">Shoot from comfortable, upright position</p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-8">
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6">
                  <strong className="text-teal-600">Don't squat or crouch while taking pictures.</strong>
                  Shoot from a regular standing position - it's comfortable for you and provides the optimal angle for the algorithm!
                  Natural positioning leads to natural, well-composed photos.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-6">
                    <h4 className="font-semibold text-teal-800 mb-3 flex items-center">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Perfect Posture
                    </h4>
                    <ul className="text-teal-700 space-y-2">
                      <li>• Stand upright in natural position</li>
                      <li>• Feet shoulder-width apart for stability</li>
                      <li>• Hold phone at comfortable height</li>
                      <li>• Slight bend in elbows, not fully extended</li>
                      <li>• Relaxed shoulders, not hunched</li>
                    </ul>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <h4 className="font-semibold text-red-800 mb-3 flex items-center">
                      <X className="h-4 w-4 mr-2" />
                      Avoid These Positions
                    </h4>
                    <ul className="text-red-700 space-y-2">
                      <li>• Squatting or crouching low</li>
                      <li>• Stretching arms fully overhead</li>
                      <li>• Leaning awkwardly to one side</li>
                      <li>• Taking photos while walking</li>
                      <li>• Uncomfortable twisted positions</li>
                    </ul>
                  </div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mt-6">
                  <h4 className="font-semibold text-yellow-800 mb-3 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Why Standing Position Works Best
                  </h4>
                  <ul className="text-yellow-800 space-y-2">
                    <li>• <strong>Natural angle:</strong> Matches how customers view jewelry</li>
                    <li>• <strong>Stability:</strong> More stable than crouched positions</li>
                    <li>• <strong>Comfort:</strong> Sustainable for multiple photos</li>
                    <li>• <strong>Consistency:</strong> Easier to maintain same angle across shots</li>
                    <li>• <strong>Algorithm optimization:</strong> Trained on naturally positioned images</li>
                  </ul>
                </div>
                {/* Example images */}
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-800 mb-4">📷 Visual Examples</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h5 className="font-medium text-red-800 mb-3 flex items-center">
                        <X className="h-4 w-4 mr-2" />
                        Crouching Position
                      </h5>
                      <img
                        src="/guidelines/posture/crouching.jpg"
                        alt="Example of person crouching while taking photo"
                        className="w-full h-48 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setSelectedImage({ src: '/guidelines/posture/crouching.jpg', alt: 'Example of person crouching while taking photo' })}
                      />
                      <p className="text-sm text-red-700 mt-2">
                        Crouching or squatting creates unstable positioning and uncomfortable angles.
                      </p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-medium text-green-800 mb-3 flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Natural Standing
                      </h5>
                      <div className="w-full h-48 bg-green-100 rounded-lg border flex items-center justify-center">
                        <div className="text-center text-green-600">
                          <User className="h-8 w-8 mx-auto mb-2" />
                          <p className="text-sm">Comfortable<br/>standing position</p>
                        </div>
                      </div>
                      <p className="text-sm text-green-700 mt-2">
                        Natural standing position provides stability and optimal viewing angle.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 8: Review Results */}
          <section id="review-results" className="mb-16">
            <div className="flex items-center mb-6">
              <div className="bg-orange-100 p-3 rounded-lg mr-4">
                <Shield className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Careful Review</h2>
                <p className="text-gray-600">Always verify results before proceeding</p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-8">
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 mb-6">
                  <strong className="text-orange-600">Our software is extremely precise, but still not perfect.</strong>
                  Always carefully review the detection results before advancing to the next showcase.
                  Your verification ensures the highest accuracy in your inventory management.
                </p>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-6">
                  <h4 className="font-semibold text-orange-800 mb-3 flex items-center">
                    <Shield className="h-4 w-4 mr-2" />
                    Review Checklist
                  </h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <ul className="text-orange-800 space-y-2">
                      <li>• ✅ <strong>All items detected:</strong> No missing jewelry</li>
                      <li>• ✅ <strong>Correct classifications:</strong> Right item types</li>
                      <li>• ✅ <strong>Accurate descriptions:</strong> Proper metal/stone identification</li>
                    </ul>
                    <ul className="text-orange-800 space-y-2">
                      <li>• ✅ <strong>No false positives:</strong> No non-jewelry items detected</li>
                      <li>• ✅ <strong>Complete boundaries:</strong> Detection boxes properly placed</li>
                      <li>• ✅ <strong>Confidence levels:</strong> Check uncertain detections</li>
                    </ul>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                    <Zap className="h-4 w-4 mr-2" />
                    Quick Actions
                  </h4>
                  <ul className="text-blue-800 space-y-2">
                    <li>• <strong>Corrections:</strong> Fix any misclassified items immediately</li>
                    <li>• <strong>Missing items:</strong> Draw detection boxes for missed jewelry</li>
                    <li>• <strong>False detections:</strong> Delete any incorrect detections</li>
                    <li>• <strong>Confidence check:</strong> Review and confirm uncertain items</li>
                    <li>• <strong>Final verification:</strong> Double-check before moving to next showcase</li>
                  </ul>
                </div>
                {/* Example images */}
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-800 mb-4">📷 Review Interface</h4>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="text-center">
                      <Shield className="h-12 w-12 mx-auto mb-4 text-blue-600" />
                      <h5 className="font-semibold text-blue-800 mb-3">
                        Always Review Your Results
                      </h5>
                      <p className="text-blue-700 mb-4">
                        Take a moment to verify all detections before proceeding to the next showcase.
                        This ensures maximum accuracy in your inventory management.
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div className="bg-white rounded p-3">
                          <CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-600" />
                          <div className="text-green-800 font-medium">All Found</div>
                        </div>
                        <div className="bg-white rounded p-3">
                          <Target className="h-5 w-5 mx-auto mb-1 text-blue-600" />
                          <div className="text-blue-800 font-medium">Correct Type</div>
                        </div>
                        <div className="bg-white rounded p-3">
                          <Eye className="h-5 w-5 mx-auto mb-1 text-purple-600" />
                          <div className="text-purple-800 font-medium">Right Details</div>
                        </div>
                        <div className="bg-white rounded p-3">
                          <Zap className="h-5 w-5 mx-auto mb-1 text-orange-600" />
                          <div className="text-orange-800 font-medium">Quick Fix</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Summary Footer */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              You're Ready to Go!
            </h3>
            <p className="text-lg text-gray-700 mb-6">
              Following these guidelines will significantly improve detection accuracy and make your inventory process faster and more reliable.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl mb-2">📸</div>
                <div className="font-semibold text-gray-900">Better Photos</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl mb-2">🎯</div>
                <div className="font-semibold text-gray-900">Higher Accuracy</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl mb-2">⚡</div>
                <div className="font-semibold text-gray-900">Faster Process</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-2xl mb-2">✅</div>
                <div className="font-semibel text-gray-900">Less Corrections</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-full">
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default GuidelinesPage